import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { useAuth } from '@/hooks/use-auth';
import { supabase } from '@/integrations/supabase/client';
import { resolveOwnerId } from '@/lib/identity/resolveOwnerId';
import { rehashPendingMedia } from '@/lib/image-pipeline/opportunisticRehash';
import { mediaService } from '@/services/media/mediaService';
import {
  type UploadFailure,
  describeUploadFailuresCapped,
  describeUploadWarning,
  uploadBatch,
  uploadFailureTitle,
} from '@/services/media/upload-batch';
import { logger } from '@/utils/logger';
import { showErrorToast, showSuccessToast, showWarningToast } from '@/utils/toast';

interface UseMediaLibraryOptions {
  folder?: string;
  collectionId?: string;
  tags?: string[];
  limit?: number;
  /** Defaults to true (matches every existing caller). Set false to skip the
   *  query entirely — e.g. the project-detail branch of ProjectAssetGrid,
   *  which renders PlanDetailView (backed by project_files) instead and
   *  never needed this collection-scoped media read. */
  enabled?: boolean;
}

export function useMediaLibrary(options?: UseMediaLibraryOptions) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});

  // Page size, not a hard cap. This query is offset-0-only by design (the
  // realtime INSERT subscription below patches the head of the list, which an
  // accumulating useInfiniteQuery cache would fight), so "load more" widens the
  // single page rather than appending a second one.
  //
  // It used to be a fixed limit with no way past it: the picker asked for 100,
  // the account had 1,462 media rows, and the other 1,362 were simply
  // unreachable. `totalCount` was already returned and no caller displayed it.
  const basePageSize = options?.limit || 50;
  const [pageSize, setPageSize] = useState(basePageSize);

  // Fetch media
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['media-library', options?.collectionId, options?.folder, options?.tags, pageSize],
    enabled: options?.enabled ?? true,
    queryFn: async () => {
      const result = await mediaService.getUserMedia({
        collectionId: options?.collectionId,
        folder: options?.folder,
        tags: options?.tags,
        limit: pageSize,
        offset: 0,
      });

      rehashPendingMedia(
        result.data.map((r) => ({
          id: r.id,
          url: mediaService.convertToMediaFile(r).url,
          type: r.type,
          metadata: r.metadata as {
            phash_status?: string;
            phash?: string;
            [k: string]: unknown;
          } | null,
        })),
      ).catch(() => {});

      return {
        media: result.data.map((record) => mediaService.convertToMediaFile(record)),
        totalCount: result.count,
      };
    },
  });

  // D19a: live subscription. /media used to require a manual refresh after a
  // background generation completed — the agent wrote the media row but the
  // query cache stayed stale. Subscribe to INSERTs on `media` for this user
  // and invalidate the list so newly-generated content appears in real time.
  //
  // Pass 10 P1 fix: Supabase JS client caches channels by name. Under
  // StrictMode + HMR + re-mounts, the cleanup removeChannel() races against
  // the next mount's `.channel(name)` — the second mount gets back the
  // cached (still 'subscribed') channel and the new `.on()` call throws
  // "cannot add postgres_changes callbacks ... after subscribe()". Appending
  // a per-mount UUID guarantees each subscription gets a fresh channel.
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    let channel: ReturnType<typeof supabase.channel> | null = null;

    void (async () => {
      // `media` RLS gates on current_account_owner_id(), so rows are stamped
      // with the account owner's id — a shared-account member's `user.id`
      // matches zero rows in the filter below.
      // resolveOwnerId() now fails closed (throws) instead of silently
      // falling back to the raw id, so this bare await needs a try/catch —
      // skip the subscription setup rather than let it become an unhandled
      // rejection.
      let ownerId: string;
      try {
        ownerId = await resolveOwnerId(user.id);
      } catch (err) {
        logger.error('useMediaLibrary: failed to resolve account owner id', err);
        return;
      }
      if (cancelled) return;

      const channelName = `media-library:${ownerId}:${crypto.randomUUID()}`;
      channel = supabase
        .channel(channelName)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'media',
            filter: `user_id=eq.${ownerId}`,
          },
          () => {
            queryClient.invalidateQueries({ queryKey: ['media-library'] });
            queryClient.invalidateQueries({ queryKey: ['media'] });
          },
        )
        .subscribe();
    })();

    return () => {
      cancelled = true;
      if (channel) void supabase.removeChannel(channel);
    };
  }, [user?.id, queryClient]);

  const lastUploadFailuresRef = useRef<UploadFailure[]>([]);
  const lastUploadTotalRef = useRef(0);

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: async ({
      files,
      folder,
      collectionId,
      tags,
      parentMessageId,
    }: {
      files: File[];
      folder?: string;
      collectionId?: string;
      tags?: string[];
      parentMessageId?: string;
    }) => {
      lastUploadFailuresRef.current = [];
      const { results, failures } = await uploadBatch(files, async (file) => {
        setUploadProgress((prev) => ({ ...prev, [file.name]: 0 }));

        const progressInterval = setInterval(() => {
          setUploadProgress((prev) => ({
            ...prev,
            [file.name]: Math.min((prev[file.name] || 0) + 20, 90),
          }));
        }, 200);

        try {
          const result = await mediaService.uploadFile(file, {
            folder,
            collectionId,
            tags,
            parentMessageId,
          });
          setUploadProgress((prev) => ({ ...prev, [file.name]: 100 }));
          return result;
        } catch (error) {
          logger.error(`Failed to upload ${file.name}:`, error);
          setUploadProgress((prev) => {
            const newProgress = { ...prev };
            delete newProgress[file.name];
            return newProgress;
          });
          throw error;
        } finally {
          clearInterval(progressInterval);
        }
      });

      if (results.length === 0 && failures.length > 0) {
        throw new Error(
          failures.length === 1
            ? failures[0].message
            : `${uploadFailureTitle(failures.length, files.length)}. ${describeUploadFailuresCapped(failures)}`,
        );
      }
      lastUploadFailuresRef.current = failures;
      lastUploadTotalRef.current = files.length;

      return results;
    },
    onSuccess: (results) => {
      queryClient.invalidateQueries({ queryKey: ['media-library'] });
      queryClient.invalidateQueries({ queryKey: ['media'] });
      queryClient.invalidateQueries({ queryKey: ['media-folders'] });
      // The Library page grid reads ['library'] — without this, an upload only
      // appears after a manual refresh (same class as the delete-path fix below).
      queryClient.invalidateQueries({ queryKey: ['library'] });
      const notices = results
        .map((result) => result.conversionNotice)
        .filter((notice): notice is string => Boolean(notice));
      const failures = lastUploadFailuresRef.current;
      if (failures.length > 0) {
        showWarningToast(
          uploadFailureTitle(failures.length, lastUploadTotalRef.current),
          describeUploadWarning({
            uploaded: results.length,
            failures,
            conversions: results.flatMap((result) =>
              result.conversion ? [result.conversion] : [],
            ),
          }),
        );
      } else {
        showSuccessToast(
          'Upload Complete',
          [
            `Successfully uploaded ${results.length} file${results.length > 1 ? 's' : ''}`,
            ...notices,
          ].join('. '),
        );
      }
      setUploadProgress({});
    },
    onError: (error: Error) => {
      showErrorToast('Upload Failed', error.message);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (mediaId: string) => {
      await mediaService.deleteMedia(mediaId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media-library'] });
      queryClient.invalidateQueries({ queryKey: ['media'] });
      queryClient.invalidateQueries({ queryKey: ['media-folders'] });
      // UXA-016: the /library grid is backed by the ['library'] key, which this
      // mutation never invalidated — a deleted image/video lingered in the grid
      // until manual refresh (the post-delete path already invalidates it).
      queryClient.invalidateQueries({ queryKey: ['library'] });
      showSuccessToast('Media Deleted', 'File removed from library');
    },
    onError: (error: Error) => {
      showErrorToast('Delete Failed', error.message);
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async ({
      mediaId,
      updates,
    }: {
      mediaId: string;
      updates: { alt_text?: string; folder?: string; tags?: string[] };
    }) => {
      await mediaService.updateMedia(mediaId, updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media-library'] });
      showSuccessToast('Media Updated', 'Changes saved successfully');
    },
    onError: (error: Error) => {
      showErrorToast('Update Failed', error.message);
    },
  });

  const createFolderMutation = useMutation({
    mutationFn: async ({
      name,
      icon,
      color,
      parentId,
    }: {
      name: string;
      icon?: string;
      color?: string;
      parentId?: string;
    }) => {
      return mediaService.createFolder(name, icon, color, parentId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media-folders'] });
      queryClient.invalidateQueries({ queryKey: ['media-folders-details'] });
      queryClient.invalidateQueries({ queryKey: ['media-library'] });
      queryClient.invalidateQueries({ queryKey: ['media'] });
      showSuccessToast('Collection Created', 'Your new collection is ready');
    },
    onError: (error: Error) => {
      showErrorToast('Create Failed', error.message);
    },
  });

  const updateFolderMutation = useMutation({
    mutationFn: async ({
      id,
      name,
      icon,
      color,
    }: {
      id: string;
      name?: string;
      icon?: string;
      color?: string;
    }) => {
      await mediaService.updateFolder(id, { name, icon, color });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media-folders'] });
      queryClient.invalidateQueries({ queryKey: ['media-folders-details'] });
      queryClient.invalidateQueries({ queryKey: ['media-library'] });
      queryClient.invalidateQueries({ queryKey: ['media'] });
      showSuccessToast('Collection Updated', 'Collection settings updated');
    },
    onError: (error: Error) => {
      showErrorToast('Update Failed', error.message);
    },
  });

  const deleteFolderMutation = useMutation({
    mutationFn: async ({ id, deleteContents }: { id: string; deleteContents?: boolean }) => {
      await mediaService.deleteFolder(id, deleteContents);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media-folders'] });
      queryClient.invalidateQueries({ queryKey: ['media-folders-details'] });
      queryClient.invalidateQueries({ queryKey: ['media-library'] });
      queryClient.invalidateQueries({ queryKey: ['media'] });
      showSuccessToast('Collection Deleted', 'Collection removed successfully');
    },
    onError: (error: Error) => {
      showErrorToast('Delete Failed', error.message);
    },
  });

  const moveToFolderMutation = useMutation({
    mutationFn: async ({
      mediaIds,
      collectionId,
    }: {
      mediaIds: string[];
      collectionId: string | null;
    }) => {
      await mediaService.moveToFolder(mediaIds, collectionId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media-folders'] });
      queryClient.invalidateQueries({ queryKey: ['media-folders-details'] });
      queryClient.invalidateQueries({ queryKey: ['media-library'] });
      queryClient.invalidateQueries({ queryKey: ['media'] });
      showSuccessToast('Files Moved', 'Items moved successfully');
    },
    onError: (error: Error) => {
      showErrorToast('Move Failed', error.message);
    },
  });

  const archiveFolderMutation = useMutation({
    mutationFn: (folderId: string) => {
      if (!user?.id) throw new Error('Not authenticated');
      return mediaService.archiveFolder(folderId, user.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media-folders'] });
      queryClient.invalidateQueries({ queryKey: ['media-folders-details'] });
    },
    onError: (error: Error) => {
      showErrorToast('Archive Failed', error.message);
    },
  });

  const unarchiveFolderMutation = useMutation({
    mutationFn: (folderId: string) => {
      if (!user?.id) throw new Error('Not authenticated');
      return mediaService.unarchiveFolder(folderId, user.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media-folders'] });
      queryClient.invalidateQueries({ queryKey: ['media-folders-details'] });
    },
    onError: (error: Error) => {
      showErrorToast('Unarchive Failed', error.message);
    },
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: async (folderIds: string[]) => {
      for (const id of folderIds) {
        await mediaService.deleteFolder(id, true);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media-folders'] });
      queryClient.invalidateQueries({ queryKey: ['media-folders-details'] });
      queryClient.invalidateQueries({ queryKey: ['media-library'] });
    },
    onError: (error: Error) => {
      showErrorToast('Delete Failed', error.message);
    },
  });

  const moveFolderMutation = useMutation({
    mutationFn: async ({ folderId, parentId }: { folderId: string; parentId: string | null }) => {
      return mediaService.moveFolderTo(folderId, parentId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media-folders'] });
      queryClient.invalidateQueries({ queryKey: ['media-folders-details'] });
    },
    onError: (error: Error) => {
      showErrorToast('Move Failed', error.message);
    },
  });

  const bulkArchiveMutation = useMutation({
    mutationFn: async (folderIds: string[]) => {
      if (!user?.id) throw new Error('Not authenticated');
      for (const id of folderIds) {
        await mediaService.archiveFolder(id, user.id);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media-folders'] });
      queryClient.invalidateQueries({ queryKey: ['media-folders-details'] });
    },
    onError: (error: Error) => {
      showErrorToast('Archive Failed', error.message);
    },
  });

  // Get folders
  const { data: folders = [] } = useQuery({
    queryKey: ['media-folders'],
    queryFn: () => mediaService.getUserFolders(),
  });

  const { data: foldersWithDetails = [] } = useQuery({
    queryKey: ['media-folders-details'],
    queryFn: () => mediaService.getUserFoldersWithDetails(),
  });

  const loadedCount = data?.media.length ?? 0;
  const totalCount = data?.totalCount || 0;

  return {
    media: data?.media || [],
    totalCount,
    /** True when the library holds more than this page currently shows. */
    hasMore: totalCount > loadedCount,
    /** Widen the page. No-op once everything is loaded. */
    loadMore: () => setPageSize((n) => (totalCount > n ? n + basePageSize : n)),
    isLoading,
    error,
    uploadProgress,
    folders,
    foldersWithDetails,

    uploadFiles: uploadMutation.mutate,
    uploadFilesAsync: uploadMutation.mutateAsync,
    isUploading: uploadMutation.isPending,

    deleteMedia: deleteMutation.mutate,
    isDeleting: deleteMutation.isPending,

    updateMedia: updateMutation.mutate,
    isUpdating: updateMutation.isPending,

    createFolder: createFolderMutation.mutate,
    isCreatingFolder: createFolderMutation.isPending,

    updateFolder: updateFolderMutation.mutate,
    isUpdatingFolder: updateFolderMutation.isPending,

    deleteFolder: deleteFolderMutation.mutate,
    isDeletingFolder: deleteFolderMutation.isPending,

    moveToFolder: moveToFolderMutation.mutate,
    isMovingToFolder: moveToFolderMutation.isPending,

    archiveFolder: archiveFolderMutation.mutate,
    isArchivingFolder: archiveFolderMutation.isPending,

    unarchiveFolder: unarchiveFolderMutation.mutate,
    isUnarchivingFolder: unarchiveFolderMutation.isPending,

    moveFolderTo: moveFolderMutation.mutate,
    isMovingFolder: moveFolderMutation.isPending,

    bulkDelete: bulkDeleteMutation.mutate,
    isBulkDeleting: bulkDeleteMutation.isPending,

    bulkArchive: bulkArchiveMutation.mutate,
    isBulkArchiving: bulkArchiveMutation.isPending,

    refetch,
  };
}

// Hook for attaching media to posts
export function useAttachMedia() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ postId, mediaUrls }: { postId: string; mediaUrls: string[] }) => {
      const { campaignService } = await import('@/services/data/CampaignService');
      return campaignService.updatePostMedia(postId, mediaUrls);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      showSuccessToast('Media Attached', 'Media added to post');
    },
    onError: (error: Error) => {
      showErrorToast('Attachment Failed', error.message);
    },
  });
}
